<template>
  <div>
    <LazySpinner v-if="pending" />
    <div v-else-if="error">{{ error }}</div>
    <LazyNotFound
      v-else-if="course === null"
      type="course"
      :id="route.params.courseId"
    />

    <div v-else-if="course">
      <main class="page-width">
        <header>
          <h1>
            {{ course.name }}
          </h1>

          <span class="button-group">
            <Button primary :href="`/courses/${course.id}/run`"
              >Run Course</Button
            ></span
          >
        </header>
        <section>
          <p>
            {{
              course.description && course.description.length > 0
                ? course.description
                : "No description provided."
            }}
          </p>
          <Chip>
            <DifficultyIcon :difficulty="course.difficulty" />
            {{ course.difficulty || "Unknown Difficulty" }}
          </Chip>
          <Chip
            ><i-material-symbols-check-circle-outline />&nbsp;
            {{ course.checkpoints.length }}</Chip
          >
          <Chip v-if="course.estimated_duration_hours">
            <i-ion-timer-outline />&nbsp;
            {{
              course.estimated_duration_hours
                ? durationTime
                : "No Duration Specified"
            }}
          </Chip>
        </section>

        <section class="primary">
          <div class="map-container">
            <Map :fit="checkpoints">
              <CheckpointMarker
                v-for="cp in checkpoints"
                :key="cp.id"
                :checkpoint="cp"
                :start="cp.id == course.start_checkpoint_id"
                :end="cp.id == course.end_checkpoint_id"
              />
              <MapConnectingLines
                v-if="course.visit_in_order"
                :checkpoints="checkpoints"
                :end-is-start="
                  course.start_checkpoint_id == course.end_checkpoint_id
                "
              />
              <LiveLocations :course-id="route.params.courseId" />
            </Map>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>
<script lang="ts" setup>
const courseId = useParam("courseId");

function getAnonCourseForView(S: ReturnType<typeof useClient>, id: string) {
  return S.from("courses")
    .select(
      `id,name,description,visit_in_order,difficulty,
       estimated_duration_hours,start_checkpoint_id,end_checkpoint_id,
       checkpoints!checkpoints_course_id_fkey ( lat, lng, id, radius, name, description )
       `
    )
    .eq("id", toValue(id))
    .eq("public", true)
    .order("order", { referencedTable: "checkpoints", ascending: true })
    .maybeSingle();
}

const route = useRoute();
function useCourseForViewAsAnon(id: MaybeRefOrGetter<string>) {
  return useSupabaseQuery({
    queryKey: computed(() => ["courses", toValue(id), "view"]),
    queryFn: (S) => getAnonCourseForView(S, toValue(id)),
  });
}

const {
  error,
  isPending: pending,
  ...courseQuery
} = useCourseForViewAsAnon(courseId);
const checkpoints = computed(() =>
  compact(courseQuery.data.value?.checkpoints)
);
const course = computed(() => courseQuery.data.value);

const durationTime = computed(() => {
  if (!course.value?.estimated_duration_hours) return "unknown";
  if (course.value.estimated_duration_hours < 2)
    return `${Math.round(course.value.estimated_duration_hours * 60)}m`;
  return `${Math.round(course.value.estimated_duration_hours)}h`;
});
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 500px;
  box-shadow: var(--subtle-shadow);
  margin-bottom: var(--grid);
}

.page-width {
  padding: var(--grid);
}

.section {
  margin-bottom: var(--grid-2);
}

.chip {
  margin-right: var(--half-grid);
  margin-bottom: var(--half-grid);
}

header {
  position: relative;
}

.button-group {
  position: absolute;
  right: 0px;
  top: 0px;
}
</style>
